import { Link, styled, Theme, Typography } from '@mui/material';
import DESIGN_COLORS from 'shared/_ui/design-colors';

/**
 * Card of a list item: white, 1px border, 14px radius (same as the project info card), no shadow.
 * A card with a link (`ListItemTitleLink`) gets hover styles and a focus ring.
 */
export const ListItemContainer = styled('div', { name: 'ListItemContainer' })(({ theme }) => ({
	// Positioned so the stretched title link (`ListItemTitleLink::after`) covers the whole card.
	position: 'relative',
	// Own stacking context, so the z-indexes of the title overlay and the copy button stay inside the card.
	isolation: 'isolate',
	padding: theme.spacing(4),
	marginBottom: theme.spacing(4),
	border: `1px solid ${DESIGN_COLORS.border}`,
	borderRadius: '0.875rem',
	backgroundColor: theme.palette.background.paper,
	transition: theme.transitions.create(['background-color', 'border-color'], { duration: 150 }),
	// The title link is the only link in a card and its stretched `::after` covers the card, so the
	// link is hovered whenever the card is: a clickable card gets the page background and a darker border.
	'&:has(a:hover)': {
		backgroundColor: DESIGN_COLORS.surfaceMuted,
		borderColor: DESIGN_COLORS.borderHover,
	},
	// Inset, because the page content scrolls and would clip an outer ring.
	'&:has(a:focus-visible)': { boxShadow: `inset 0 0 0 2px ${DESIGN_COLORS.focusRing}` },
}));

export const ListItemImage = styled('div', { name: 'ListItemImage' })({
	gridArea: 'image',
	'& img': {
		// TODO: remove
		height: 'unset',
		width: 'unset',
	},
});

export const ListItemTitle = styled(Typography, { name: 'ListItemTitle' })({
	gridArea: 'title',
	fontSize: '1rem',
	fontWeight: 'bold',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

/**
 * Item title link. Its `::after` stretches over the card, so the whole card is clickable while the
 * title stays the only focusable element; hovering the card hovers the link and underlines the title.
 * Interactive content inside the card (e.g. the copy button of the id) must be `position: relative`
 * with `zIndex: 3`.
 */
export const ListItemTitleLink = styled(Link, { name: 'ListItemTitle' })({
	gridArea: 'title',
	fontSize: '1rem',
	fontWeight: 'bold',
	width: 'fit-content',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	'&::after': { content: '""', position: 'absolute', inset: 0, zIndex: 2 },
	'&:focus-visible': { outline: 'none' },
});
ListItemTitleLink.defaultProps = { ...ListItemTitleLink.defaultProps, underline: 'hover' };

/** Props for `CopyToClipboardValue` that make an item id monospace and muted, with a copy button above the title link. */
export const listItemIdProps = {
	sx: (theme: Theme) => ({
		'& .MuiTypography-root': {
			color: DESIGN_COLORS.faint,
			fontFamily: theme.typography.fontFamilyMono,
			fontSize: '0.8125rem',
		},
	}),
	sxButton: { position: 'relative', zIndex: 3 },
} as const;

type ListBoxProps = {
	gridArea?: string;
};

export const ListItemText = styled(Typography, { name: 'ListItemText' })({
	color: DESIGN_COLORS.muted,
	fontSize: '0.8125rem',
});

export const ListItemBox = styled('div', { name: 'ListItemBox' })<ListBoxProps>(({ gridArea }) => ({
	gridArea,
}));
export const ListItemBoxUpMd = styled(ListItemBox, { name: 'ListItemBoxUpMd' })(({ theme }) => ({
	[theme.breakpoints.down('md')]: { display: 'none' },
}));
export const ListItemBoxUpLg = styled(ListItemBox, { name: 'ListItemBoxUpLg' })(({ theme }) => ({
	[theme.breakpoints.down('lg')]: { display: 'none' },
}));
export const ListItemBoxDnMd = styled(ListItemBox, { name: 'ListItemBoxDnMd' })(({ theme }) => ({
	[theme.breakpoints.up('md')]: { display: 'none' },
}));
export const ListItemBoxDnLg = styled(ListItemBox, { name: 'ListItemBoxDnLg' })(({ theme }) => ({
	[theme.breakpoints.up('lg')]: { display: 'none' },
}));

/** Icons on the right of an item; every icon inside is 1.5rem, 1.75rem from `sm`. */
export const ListItemBoxIcons = styled(ListItemBox, { name: 'ListItemBoxIcons' })(({ theme }) => ({
	gridArea: 'icons',
	display: 'flex',
	gap: theme.spacing(1),
	'& .MuiSvgIcon-root': {
		width: '1.5rem',
		height: '1.5rem',
		fontSize: '1.5rem',
		[theme.breakpoints.up('sm')]: { width: '1.75rem', height: '1.75rem', fontSize: '1.75rem' },
	},
}));
